import React, { useEffect, useState, useCallback } from 'react';
import { productAPI, customerAPI, warehouseAPI } from '../services/api';
import { getProductStatusBadge, getConditionBadge, formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';

// ── Receive Product Modal ─────────────────────────────────────────────────────
function ReceiveModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    customerId: '', productName: '', sku: '',
    supplierTrackingNumber: '', totalQuantityReceived: 1,
    unit: 'pcs', unitPrice: '', conditionStatus: 'GOOD', notes: '',
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    customerAPI.getAll().then(r => setCustomers(r.data.data || []));
  }, []);

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await productAPI.receive({
        ...form,
        customerId: Number(form.customerId),
        totalQuantityReceived: Number(form.totalQuantityReceived),
        unitPrice: form.unitPrice ? Number(form.unitPrice) : null,
      });
      toast.success('Product received!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to receive product');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Receive Product</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Customer *</label>
              <select className="form-select" value={form.customerId} onChange={e => f('customerId', e.target.value)} required>
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.customerCode})</option>)}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Product Name *</label>
              <input className="form-input" value={form.productName} onChange={e => f('productName', e.target.value)} placeholder="Nike Air Max Shoes" required />
            </div>
            <div className="form-group">
              <label className="form-label">SKU / Item Code</label>
              <input className="form-input" value={form.sku} onChange={e => f('sku', e.target.value)} placeholder="NIKE-AM-42-RED" />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier Tracking No.</label>
              <input className="form-input" value={form.supplierTrackingNumber} onChange={e => f('supplierTrackingNumber', e.target.value)} placeholder="SF1234567890CN" />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity Received *</label>
              <input className="form-input" type="number" min="1" value={form.totalQuantityReceived} onChange={e => f('totalQuantityReceived', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <select className="form-select" value={form.unit} onChange={e => f('unit', e.target.value)}>
                {['pcs','pairs','sets','kg','boxes','rolls','meters'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Unit Price (USD)</label>
              <input className="form-input" type="number" step="0.01" value={form.unitPrice} onChange={e => f('unitPrice', e.target.value)} placeholder="25.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-select" value={form.conditionStatus} onChange={e => f('conditionStatus', e.target.value)}>
                <option value="GOOD">Good</option>
                <option value="DAMAGED">Damaged</option>
                <option value="PARTIAL">Partial</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-input" value={form.notes} onChange={e => f('notes', e.target.value)} rows={2} style={{ resize: 'vertical' }} placeholder="Any remarks about this product..." />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
              Receive Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Sort / Verify Modal ───────────────────────────────────────────────────────
function SortModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    productName: product.productName || '',
    sku: product.sku || '',
    totalQuantityReceived: product.totalQuantityReceived || 1,
    unit: product.unit || 'pcs',
    unitPrice: product.unitPrice || '',
    conditionStatus: product.conditionStatus || 'GOOD',
    notes: product.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await productAPI.sort(product.id, {
        ...form,
        totalQuantityReceived: Number(form.totalQuantityReceived),
        unitPrice: form.unitPrice ? Number(form.unitPrice) : null,
      });
      toast.success('Product sorted and verified!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Sort & Verify Product</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--text-muted)' }}>
          Code: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{product.productCode}</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Product Name</label>
              <input className="form-input" value={form.productName} onChange={e => f('productName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">SKU</label>
              <input className="form-input" value={form.sku} onChange={e => f('sku', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Actual Quantity</label>
              <input className="form-input" type="number" min="1" value={form.totalQuantityReceived} onChange={e => f('totalQuantityReceived', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Unit Price</label>
              <input className="form-input" type="number" step="0.01" value={form.unitPrice} onChange={e => f('unitPrice', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-select" value={form.conditionStatus} onChange={e => f('conditionStatus', e.target.value)}>
                <option value="GOOD">Good</option>
                <option value="DAMAGED">Damaged</option>
                <option value="PARTIAL">Partial</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-input" value={form.notes} onChange={e => f('notes', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
              Save Verification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Pack Into Carton Modal ────────────────────────────────────────────────────
function PackModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState({ cartonId: '', quantity: 1, notes: '' });
  const [cartons, setCartons] = useState([]);
  const [loading, setLoading] = useState(false);
  const remaining = product.totalQuantityReceived - product.totalQuantityPacked;

  useEffect(() => {
    warehouseAPI.getAll().then(r => {
      // Only cartons that have a barcode (registered)
      const eligible = (r.data.data || []).filter(c =>
        ['RECEIVED', 'SORTING', 'REGISTERED', 'GROUPED'].includes(c.status)
      );
      setCartons(eligible);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Number(form.quantity) > remaining) {
      return toast.error(`Max ${remaining} units can be packed`);
    }
    setLoading(true);
    try {
      await productAPI.pack(product.id, {
        cartonId: Number(form.cartonId),
        quantity: Number(form.quantity),
        notes: form.notes,
      });
      toast.success(`${form.quantity} ${product.unit} packed into carton!`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Packing failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Pack into Carton</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* Product info */}
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15, marginBottom: 6 }}>{product.productName}</div>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-muted)' }}>
            <span>Total: <strong style={{ color: 'var(--text-primary)' }}>{product.totalQuantityReceived} {product.unit}</strong></span>
            <span>Packed: <strong style={{ color: 'var(--warning)' }}>{product.totalQuantityPacked} {product.unit}</strong></span>
            <span>Remaining: <strong style={{ color: 'var(--success)' }}>{remaining} {product.unit}</strong></span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Select Carton *</label>
            <select className="form-select" value={form.cartonId} onChange={e => setForm(p => ({ ...p, cartonId: e.target.value }))} required>
              <option value="">Choose a carton...</option>
              {cartons.map(c => (
                <option key={c.id} value={c.id}>
                  {c.grnNumber} {c.shippingMark ? `— ${c.shippingMark}` : ''} [{c.status}]
                </option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Only cartons in active status are shown
            </span>
          </div>
          <div className="form-group">
            <label className="form-label">Quantity to Pack *</label>
            <input
              className="form-input"
              type="number" min="1" max={remaining}
              value={form.quantity}
              onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
              required
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Max: {remaining} {product.unit} remaining
            </span>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional packing notes..." />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !form.cartonId}>
              {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
              Pack into Carton
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── View Carton Distribution Modal ────────────────────────────────────────────
function CartonDistributionModal({ product, onClose }) {
  const [cartons, setCartons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productAPI.getCartonsByProduct(product.id)
      .then(r => setCartons(r.data.data || []))
      .catch(() => toast.error('Failed to load carton distribution'))
      .finally(() => setLoading(false));
  }, [product.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Carton Distribution</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{product.productName}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {product.productCode} — {product.totalQuantityPacked}/{product.totalQuantityReceived} {product.unit} packed
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>
        ) : cartons.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">▣</div>
            <div className="empty-state-text">Not packed into any carton yet</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cartons.map((cp, i) => (
              <div key={cp.id} style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: `fadeIn 0.3s ease ${i * 50}ms both`,
              }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>
                    Carton ID: {cp.cartonId}
                  </div>
                  {cp.notes && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{cp.notes}</div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Packed: {cp.packedAt ? new Date(cp.packedAt).toLocaleString() : '—'}
                  </div>
                </div>
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 22,
                  fontWeight: 800,
                  color: 'var(--accent)',
                }}>
                  {cp.quantity}
                  <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>{product.unit}</span>
                </div>
              </div>
            ))}

            {/* Summary bar */}
            <div style={{
              background: 'rgba(59,130,246,0.08)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 8,
              padding: '10px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>Total packed across {cartons.length} carton(s)</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 600 }}>
                {cartons.reduce((s, c) => s + c.quantity, 0)} {product.unit}
              </span>
            </div>
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN Products Page ────────────────────────────────────────────────────────
export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    productAPI.getAll()
      .then(r => setProducts(r.data.data || []))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (type, product = null) => {
    setSelected(product);
    setModal(type);
  };

  const closeModal = () => { setModal(null); setSelected(null); };

  const filtered = products.filter(p => {
    const matchSearch = !search ||
      p.productName?.toLowerCase().includes(search.toLowerCase()) ||
      p.productCode?.includes(search) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Summary counts
  const counts = {
    total: products.length,
    received: products.filter(p => p.status === 'RECEIVED').length,
    sorting: products.filter(p => p.status === 'SORTING').length,
    partial: products.filter(p => p.status === 'PARTIALLY_PACKED').length,
    full: products.filter(p => p.status === 'FULLY_PACKED').length,
  };

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Products</h1>
        <p className="page-subtitle">Manage received products and pack them into cartons</p>
      </div>

      {/* Summary cards */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 24 }}>
        {[
          { label: 'Total Products', value: counts.total, color: 'var(--accent)' },
          { label: 'Pending Sort', value: counts.received, color: 'var(--warning)' },
          { label: 'Sorting', value: counts.sorting, color: '#8b5cf6' },
          { label: 'Partially Packed', value: counts.partial, color: 'var(--accent-2)' },
          { label: 'Fully Packed', value: counts.full, color: 'var(--success)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderTop: `2px solid ${s.color}`, animation: `fadeIn 0.4s ease ${i * 60}ms both` }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 28 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Action bar */}
      <div className="action-bar">
        <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
          <input
            className="form-input" style={{ maxWidth: 260 }}
            placeholder="Search name, code, SKU..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <select
            className="form-select" style={{ maxWidth: 200 }}
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="RECEIVED">Received</option>
            <option value="SORTING">Sorting</option>
            <option value="PARTIALLY_PACKED">Partially Packed</option>
            <option value="FULLY_PACKED">Fully Packed</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => openModal('receive')}>
          + Receive Product
        </button>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Product Code</th>
              <th>Product Name</th>
              <th>SKU</th>
              <th>Customer</th>
              <th>Qty Received</th>
              <th>Qty Packed</th>
              <th>Remaining</th>
              <th>Unit</th>
              <th>Condition</th>
              <th>Status</th>
              <th>Received At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ textAlign: 'center', padding: 48 }}>
                <span className="spinner" />
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={12}>
                <div className="empty-state">
                  <div className="empty-state-icon">◈</div>
                  <div className="empty-state-text">No products found</div>
                </div>
              </td></tr>
            ) : filtered.map(p => {
              const remaining = p.totalQuantityReceived - p.totalQuantityPacked;
              return (
                <tr key={p.id}>
                  <td>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)' }}>
                      {p.productCode}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500, color: 'var(--text-primary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.productName}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.sku || '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.customerId}</td>
                  <td>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {p.totalQuantityReceived}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--warning)' }}>
                      {p.totalQuantityPacked}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      color: remaining === 0 ? 'var(--success)' : remaining === p.totalQuantityReceived ? 'var(--danger)' : 'var(--accent-2)',
                    }}>
                      {remaining}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{p.unit}</td>
                  <td><span className={`badge ${getConditionBadge(p.conditionStatus)}`}>{p.conditionStatus}</span></td>
                  <td><span className={`badge ${getProductStatusBadge(p.status)}`}>{p.status?.replace('_', ' ')}</span></td>
                  <td style={{ fontSize: 12 }}>{formatDateTime(p.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {/* Sort button — only for RECEIVED status */}
                      {p.status === 'RECEIVED' && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openModal('sort', p)}
                          style={{ fontSize: 11 }}
                        >Sort</button>
                      )}
                      {/* Pack button — for RECEIVED, SORTING, PARTIALLY_PACKED */}
                      {['RECEIVED','SORTING','PARTIALLY_PACKED'].includes(p.status) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openModal('pack', p)}
                          style={{ fontSize: 11 }}
                        >Pack</button>
                      )}
                      {/* View distribution — if any packed */}
                      {p.totalQuantityPacked > 0 && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openModal('distribution', p)}
                          style={{ fontSize: 11 }}
                        >Cartons</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Flow guide */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Product Flow Guide
        </h3>
        <div style={{ display: 'flex', gap: 0, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { step: '1', label: 'Receive', sub: 'Product arrives at China warehouse', color: 'var(--warning)', status: 'RECEIVED' },
            { step: '→', label: '', sub: '', color: 'var(--text-muted)', status: null },
            { step: '2', label: 'Sort & Verify', sub: 'Check qty, condition, fix details', color: '#8b5cf6', status: 'SORTING' },
            { step: '→', label: '', sub: '', color: 'var(--text-muted)', status: null },
            { step: '3', label: 'Pack', sub: 'Assign to 1 or more cartons', color: 'var(--accent-2)', status: 'PARTIALLY_PACKED' },
            { step: '→', label: '', sub: '', color: 'var(--text-muted)', status: null },
            { step: '4', label: 'Fully Packed', sub: 'All units assigned to cartons', color: 'var(--success)', status: 'FULLY_PACKED' },
          ].map((item, i) => (
            item.step === '→' ? (
              <div key={i} style={{ fontSize: 20, color: 'var(--text-muted)', padding: '0 8px' }}>→</div>
            ) : (
              <div key={i} style={{
                background: 'var(--bg-secondary)',
                border: `1px solid ${item.color}30`,
                borderRadius: 10,
                padding: '12px 16px',
                minWidth: 140,
                flex: 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: item.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, color: '#fff',
                    flexShrink: 0,
                  }}>{item.step}</div>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: item.color }}>{item.label}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 32 }}>{item.sub}</div>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Modals */}
      {modal === 'receive' && (
        <ReceiveModal onClose={closeModal} onSaved={() => { closeModal(); load(); }} />
      )}
      {modal === 'sort' && selected && (
        <SortModal product={selected} onClose={closeModal} onSaved={() => { closeModal(); load(); }} />
      )}
      {modal === 'pack' && selected && (
        <PackModal product={selected} onClose={closeModal} onSaved={() => { closeModal(); load(); }} />
      )}
      {modal === 'distribution' && selected && (
        <CartonDistributionModal product={selected} onClose={closeModal} />
      )}
    </div>
  );
}
